import { Edge, Node, Position } from '@xyflow/react';
import {
	NODE_HEIGHT,
	NODE_WIDTH,
} from '@dealstack/frontend/app/pages/Project/Cap/CapChartPage/CapChart/Nodes';
import dagre from '@dagrejs/dagre';
import {
	CapChartEdgeData,
	CapChartEdgeLabelValue,
	EdgeRes,
	EdgeTypes,
	NodeData,
	NodeRes,
	NodeTypes,
} from '@dealstack/frontend/app/pages/Project/Cap/CapChartPage/CapChart/chart.model.ts';
import {
	CAP_TABLE_NUMBER_FORMAT,
	CAP_TABLE_VALUE_DISPLAY_MODE,
	CAP_TABLE_VALUES_HELD_TYPE,
} from '@dealstack/frontend/app/pages/Project/Cap/CapTablePage/common/tableSettings.ts';
import { formatCapNumber } from '@dealstack/frontend/app/pages/Project/Cap/CapTablePage/common/tableUtils.ts';
import type { CapChartLocalStorageSettings } from '@dealstack/frontend/app/pages/Project/Cap/CapChartPage/capChartSettings.types.ts';
import { formatNumber, numberToUiPercent } from '@dealstack/shared/lib/format/number.ts';
import { LegalFormType } from '@dealstack/shared/lib/generated';

type ChartXPosition = {
	chartMinX: number;
	chartMaxX: number;
};

export const getLayoutedElements = (
	nodes: NodeRes[],
	edges: EdgeRes[],
	dagreGraph: dagre.graphlib.Graph,
	chartSettings: CapChartLocalStorageSettings
): { nodes: Node<NodeData>[]; edges: Edge<CapChartEdgeData>[] } => {
	nodes.forEach((node) => {
		dagreGraph.setNode(node.id, { width: NODE_WIDTH, height: NODE_HEIGHT });
	});

	edges.forEach((edge) => {
		dagreGraph.setEdge(edge.fromNodeId, edge.toNodeId);
	});

	dagre.layout(dagreGraph);

	const newNodes = nodes.map((node) => {
		const loanEdges = edges.filter((e) => e.type === 'loan');
		return makeNode(node, dagreGraph, loanEdges);
	});

	const newEdges = edges.map((e) => makeEdge(e, newNodes, chartSettings));

	return { nodes: newNodes, edges: newEdges };
};

const makeNode = (
	node: NodeRes,
	dagreGraph: dagre.graphlib.Graph,
	loanEdges: EdgeRes[]
): Node<NodeData> => {
	const nodeWithPosition = dagreGraph.node(node.id);
	let loanSourceHandlePosition: Position | null = null;

	const sourceLoanEdges = loanEdges.filter((edge) => edge.fromNodeId === node.id);
	const targetLoanEdges = loanEdges.filter((edge) => edge.toNodeId === node.id);
	const hasSourceLoan = !!sourceLoanEdges?.length;
	const hasTargetLoan = !!targetLoanEdges?.length;

	if (hasSourceLoan) {
		loanSourceHandlePosition = isDagreNodeEndAfterCenter(nodeWithPosition, dagreGraph)
			? Position.Right
			: Position.Left;
	}
	return {
		...node,
		id: node.id,
		type: getLegalType(node.legalFormType),
		data: {
			label: node.name,
			registeredName: node.name,
			registeredId: node.registrationNumber,
			countryCode: node.jurisdiction,
			isMipGroup: node.isMipGroup,
			loanData: {
				source: hasSourceLoan,
				target: hasTargetLoan,
				sourcePosition: loanSourceHandlePosition,
			},
		},
		position: {
			x: (nodeWithPosition?.x || 0) - (nodeWithPosition.width || 0) / 2,
			y: (nodeWithPosition?.y || 0) - (nodeWithPosition.height || 0) / 2,
		},
	};
};

export const getNewEdgePosition = (
	existingNode: Node<NodeData>,
	chartMinX: number,
	chartMaxX: number,
	loanEdges: Edge[]
) => {
	let loanSourceHandlePosition: Position | null = null;
	const sourceLoanEdges = loanEdges.filter((edge) => edge.source === existingNode.id);
	const hasSourceLoan = !!sourceLoanEdges?.length;

	if (!existingNode?.data?.loanData || !hasSourceLoan) {
		return null;
	}

	if (hasSourceLoan) {
		const nodeX = existingNode.position.x;
		const nodeAfterCenter = isNodeEndAfterCenter(
			nodeX,
			{
				chartMinX,
				chartMaxX,
			},
			existingNode.width
		);
		loanSourceHandlePosition = nodeAfterCenter ? Position.Right : Position.Left;
	}

	if (existingNode.data.loanData.sourcePosition === loanSourceHandlePosition) {
		return null;
	}

	return {
		position: loanSourceHandlePosition,
		sourceLoanEdges,
	};
};

const makeEdge = (
	edge: EdgeRes,
	nodes: Node<NodeData>[],
	chartSettings: CapChartLocalStorageSettings
): Edge<CapChartEdgeData> => {
	const isDebt = edge.type === 'loan';
	const type = isDebt ? EdgeTypes.DEBT : EdgeTypes.EQUITY;

	return {
		id: `${edge.fromNodeId}-${edge.toNodeId}-${type}`,
		source: edge.fromNodeId,
		target: edge.toNodeId,
		sourceHandle: isDebt ? 'source-debt' : 'source',
		targetHandle: makeTargetHandleId(isDebt, edge.fromNodeId, edge.toNodeId, nodes),
		type,
		data: { ...edge, labelValues: makeEdgeLabelValues(edge, chartSettings) },
		style: {
			stroke: '#d3d8da',
		},
	};
};

// A real but tiny share (e.g. 12 of a million votes) would otherwise read as 0.00%.
const formatEdgePercent = (value: number): string =>
	value !== 0 && Math.round(Math.abs(value) * 100) === 0
		? '<0.01%'
		: numberToUiPercent(value, true) ?? '';

// Votes keep the cap table's two decimals in full; units keep every decimal there too.
const formatEdgeNumber = (
	value: number,
	valuesHeldType: CAP_TABLE_VALUES_HELD_TYPE,
	numberFormat: CAP_TABLE_NUMBER_FORMAT
): string =>
	valuesHeldType === CAP_TABLE_VALUES_HELD_TYPE.VOTES &&
	numberFormat === CAP_TABLE_NUMBER_FORMAT.FULL
		? formatNumber({ value, returnNumericalZero: true })
		: formatCapNumber(value, numberFormat);

const getEdgeValue = (
	edge: EdgeRes,
	valuesHeldType: CAP_TABLE_VALUES_HELD_TYPE,
	showPercentages: boolean
): number => {
	const isVotes = valuesHeldType === CAP_TABLE_VALUES_HELD_TYPE.VOTES;
	if (showPercentages) {
		return isVotes ? edge.votesPercent : edge.costPercent;
	}
	return isVotes ? edge.votes : edge.totalHeld;
};

/** One formatted value per selected type; empty for loans and for edges where every value is zero. */
export const makeEdgeLabelValues = (
	edge: EdgeRes,
	settings: CapChartLocalStorageSettings
): CapChartEdgeLabelValue[] => {
	if (edge.type === 'loan') {
		return [];
	}

	const showPercentages = settings.valueDisplayMode === CAP_TABLE_VALUE_DISPLAY_MODE.PERCENTAGE;
	const values = settings.valuesHeldTypes.map((valuesHeldType) => ({
		valuesHeldType,
		value: getEdgeValue(edge, valuesHeldType, showPercentages) || 0,
	}));

	if (values.every(({ value }) => value === 0)) {
		return [];
	}

	return values.map(({ valuesHeldType, value }) => ({
		valuesHeldType,
		text: showPercentages
			? formatEdgePercent(value)
			: formatEdgeNumber(value, valuesHeldType, settings.numberFormat),
	}));
};

export const makeTargetHandleId = (
	isDebt: boolean,
	sourceNodeId: string,
	targetNodeId: string,
	nodes: Node<NodeData>[],
	sourcePosition?: Position
) => {
	if (!isDebt) {
		return 'target';
	}

	const edgeTargetNode = nodes.find((n) => n.id === targetNodeId);
	const edgeSourceNode = nodes.find(
		(n) => n.id === sourceNodeId && targetNodeId === edgeTargetNode?.id
	);
	const targetPosition = sourcePosition || edgeSourceNode?.data?.loanData?.sourcePosition;

	return `target-debt-${targetPosition}`;
};

const getLegalType = (legalForm?: LegalFormType | null): NodeTypes => {
	switch (legalForm) {
		case LegalFormType.PARTNERSHIP:
			return NodeTypes.PARTNERSHIP;
		case LegalFormType.COMPANY:
			return NodeTypes.COMPANY;
		case LegalFormType.TRUST:
			return NodeTypes.TRUST;
		default:
			return NodeTypes.OTHER;
	}
};

const isNodeEndAfterCenter = (
	nodeX: number,
	{ chartMinX, chartMaxX }: ChartXPosition,
	nodeWidth: number = NODE_WIDTH
): boolean => {
	return nodeX - nodeWidth / 2 > (chartMinX + chartMaxX) / 2;
};

const isDagreNodeEndAfterCenter = (
	nodeWithPosition: dagre.Node,
	graphGraph: dagre.graphlib.Graph
): boolean => {
	const graph = graphGraph.graph();
	const graphHorizontalCenter = (graph.width || 0) / 2;
	const nodeStartPosition = nodeWithPosition?.x;

	return nodeStartPosition > graphHorizontalCenter;
};
